import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { handleApiCall } from "src/_config";

// Import tab components
import InvoicesTab from "./tabs/InvoicesTab";
import InfoTab from "./tabs/InfoTab";
import PaymentMethodsTab from "./tabs/PaymentMethodsTab";
import PaymentSidebar from "./components/PaymentSidebar";
import { hasPermission } from "src/_common/utils/permissionsUtils";

interface InvoiceData {
  invoiceId: string;
  date: string;
  product: string;
  transactionAmount: number;
  taxAmount: number;
  status: 'Success' | 'Refunded' | 'Failed' | 'Pending';
  method: string;
  invoiceFile?: string;
}

interface BillingMainProps { }

const BillingMain: React.FC<BillingMainProps> = () => {
  const history = useHistory();
  const userApi = useUserApi();
  const toast = useToaster();
  const userSelector = useAppUserDetailsSelector();
  const { t } = useTranslation("settings");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();


  const [activeTab, setActiveTab] = useState<'invoices' | 'info' | 'paymentMethods'>('invoices');
  const [isLoading, setIsLoading] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    nameOnCard: '',
    country: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    stateRegion: '',
    setAsDefault: true
  });

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === 'member') {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadBillingInvoices = canAccessMenu('read_billing_invoices');
  const canReadBillingInfo = canAccessMenu('read_billing_info');
  const canReadBillingPaymentMethods = canAccessMenu('read_billing_payment_methods');

   const handleBackClick = () => {
    history.goBack();
  };

  const handleAddPaymentClick = () => {
    setShowAddPaymentModal(true);
  };

  const handleCloseModal = () => {
    setShowAddPaymentModal(false);
    setPaymentForm({
      cardNumber: '',
      expiryDate: '',
      cvc: '',
      nameOnCard: '',
      country: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      stateRegion: '',
      setAsDefault: true
    });
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setPaymentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitPayment = () => {
    // Handle payment method submission
    console.log('Payment form:', paymentForm);
    toast.success(t("billing.messages.paymentMethodAdded"));
    handleCloseModal();
  };

  return (
    <div className="dashboard-main member-page-wrapper">
      {/* Header Section */}
      <div className="drjob-page-header">
        <div className="drjob-header-left">
          <button className="back-button d-none" onClick={handleBackClick}>
            {t("billing.backButton")}
          </button>
          <h1 className="drjob-page-title">{t("billing.title")}</h1>
        </div>
      </div>

      <div className="drjob-member-list">
        <div className="drjob-table-container">
          {/* Tab Navigation */}
          <div className="billing-tabs">
            {canReadBillingInvoices && <button
              className={`billing-tab ${activeTab === 'invoices' ? 'active' : ''}`}
              onClick={() => setActiveTab('invoices')}
            >
              {t("billing.tabs.invoices")}
            </button>
            }
            {canReadBillingInfo && <button
              className={`billing-tab ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              {t("billing.tabs.info")}
            </button>
            }
            {canReadBillingPaymentMethods && <button
              className={`billing-tab d-none ${activeTab === 'paymentMethods' ? 'active' : ''}`}
              onClick={() => setActiveTab('paymentMethods')}
            >
              {t("billing.tabs.paymentCards")}
            </button>
            }
          </div>

          {/* Tab Content */}
          {activeTab === 'invoices' && <InvoicesTab />}
          {activeTab === 'info' && <InfoTab userSelector={userSelector} />}
          {activeTab === 'paymentMethods' && <PaymentMethodsTab onAddPaymentClick={handleAddPaymentClick} />}
        </div>
      </div>

      {/* Payment Sidebar Component */}
      <PaymentSidebar
        isOpen={showAddPaymentModal}
        paymentForm={paymentForm}
        onClose={handleCloseModal}
        onFormChange={handleFormChange}
        onSubmit={handleSubmitPayment}
      />
    </div>
  );
};

export default BillingMain;